<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            360 virtual tour image resolution and file size specifications
            determine how sharp your tour looks on different devices, how fast
            it loads, and whether it meets the technical requirements of
            platforms like Matterport, Google Street View, Kuula, and Travvir.
            Using the wrong resolution costs you either quality (too low) or
            loading speed and storage costs (too high). This guide covers the
            exact specifications you need for every major platform and use case.
          </p>
        </section>

        <section>
          <h2>Understanding 360 Image Resolution</h2>

          <h3>Why 360 Images Need Higher Resolution Than Standard Photos</h3>
          <p>
            A standard photograph displays as a flat image. When you view a
            standard photo at full screen on a 4K monitor (3840 × 2160 pixels),
            the entire image fills the screen.
          </p>
          <p>
            A 360 photo is displayed differently: the viewer shows only a
            portion of the full image at any given time — roughly the equivalent
            of a standard photograph's field of view, extracted from the larger
            spherical source. For a 360 viewer with a 90° horizontal field of
            view, you're displaying 90° of a 360° sphere — meaning
            <strong>only 25% of the full image width</strong> is visible at
            once.
          </p>
          <p>
            This means that for a 360 photo to look as sharp as a standard photo
            when viewed, it needs <strong>4× the resolution</strong> of the
            equivalent standard image. A 360 photo that looks good in a virtual
            tour viewer needs to be a minimum of 8,000 × 4,000 pixels (32
            megapixels) to deliver sharp results at normal viewing field-of-view
            levels.
          </p>

          <h3>The Equirectangular Aspect Ratio</h3>
          <p>
            360 photos in equirectangular projection always have a
            <strong>2:1 aspect ratio</strong> — twice as wide as they are tall.
            Common standard sizes: <strong>4096 × 2048</strong> (minimum for
            acceptable quality), <strong>8192 × 4096</strong> (standard high
            quality), <strong>11520 × 5760</strong> (high-end professional),
            <strong>16384 × 8192</strong> (maximum quality, requires advanced
            camera or stitching). Any image that doesn't maintain the 2:1 ratio
            will appear distorted in 360 viewers — check your export settings to
            ensure this ratio is preserved.
          </p>
        </section>

        <section>
          <h2>Platform-Specific Resolution Requirements</h2>

          <h3>Google Street View / Google Business Profile</h3>
          <p>
            <strong>Minimum resolution:</strong> 7.5 megapixels (no minimum
            dimension specified, but files below approximately 3000 × 1500 are
            rejected). <strong>Maximum upload size:</strong> 75 megapixels.
            <strong>Recommended resolution:</strong> 8192 × 4096 (33 megapixels)
            for optimal display quality in Google Maps and Google Business
            Profile viewer. <strong>File format:</strong> JPEG with embedded XMP
            photo sphere metadata. Google compresses all uploads on ingestion —
            uploading at higher resolution than required does not increase
            delivered quality beyond their compression threshold.
          </p>

          <h3>Facebook 360 Photos</h3>
          <p>
            <strong>Minimum resolution:</strong> 2048 × 1024.
            <strong>Maximum upload size:</strong> 30 MB.
            <strong>Recommended resolution:</strong> 6000 × 3000 for good
            quality in Facebook's viewer. <strong>Note:</strong> Facebook
            applies aggressive compression on upload, reducing visible quality
            significantly. Uploading at maximum resolution minimises but does
            not eliminate this quality loss.
          </p>

          <h3>YouTube 360 Video</h3>
          <p>
            360 video, not photos, but for reference:
            <strong>minimum effective resolution</strong> for acceptable quality
            is 5760 × 2880 (8K). Lower resolutions produce noticeably pixelated
            results due to the portion-of-sphere display method described above.
          </p>

          <h3>Travvir Virtual Tours</h3>
          <p>
            Travvir accepts equirectangular 360 photos with
            <strong>recommended resolution of 8192 × 4096</strong> for best
            quality in the tour viewer. The platform handles compression and
            delivery optimisation automatically, serving appropriate quality
            tiers based on the viewer's device and connection speed. Files up to
            50 MB are accepted in JPEG format.
          </p>

          <h3>Matterport</h3>
          <p>
            Matterport uses its own capture hardware and process — uploaded 360
            images for use in Matterport tours require the Matterport Link
            feature or third-party integration. For standard use, Matterport
            tour quality is determined by capture hardware, not upload
            resolution.
          </p>
        </section>

        <section>
          <h2>File Format and Compression</h2>

          <h3>JPEG vs PNG for 360 Photos</h3>
          <p>
            <strong>JPEG</strong> is the correct format for 360 photos in almost
            all cases. JPEG's compression reduces file sizes to manageable
            levels (4–25 MB for a high-quality 360 image) and all major 360
            platforms expect JPEG input. The XMP metadata required to identify a
            file as a 360 image is embedded in the JPEG header — apps and
            cameras do this automatically.
          </p>
          <p>
            <strong>PNG</strong> is lossless but produces enormous file sizes
            for 360 images (often 100–400 MB) with no visible quality
            improvement over high-quality JPEG at the same resolution. Do not
            use PNG for 360 virtual tour images.
          </p>

          <h3>JPEG Quality Setting</h3>
          <p>
            When exporting 360 images manually: use JPEG quality
            <strong>85–92%</strong>. This range provides visually lossless
            quality at manageable file sizes. Reducing quality below 80% saves
            minimal additional file size while introducing visible compression
            artefacts (blocking and banding) that are particularly obvious in
            large flat-coloured areas like skies, ceilings, and walls.
          </p>
        </section>

        <section>
          <h2>Smartphone 360 Photo Resolution Output</h2>

          <h3>What Modern Smartphones Produce</h3>
          <p>
            Smartphone 360 apps using guided multi-frame capture typically
            produce output in the range of
            <strong>6000 × 3000 to 10000 × 5000 pixels</strong>, depending on
            the phone's camera resolution and the number of frames captured in
            the sequence. High-end smartphones (iPhone 15 Pro, Samsung S24
            Ultra, Google Pixel 8 Pro) running Travvir at maximum quality
            produce output in the <strong>8000–10000 pixel wide range</strong> —
            meeting or exceeding all major platform quality requirements.
          </p>

          <h3>Dedicated 360 Camera Output</h3>
          <p>
            Entry-level: Ricoh Theta SC2 → 5376 × 2688 pixels. This is below
            ideal for high-quality tour viewing but acceptable for most business
            applications. Mid-range: Insta360 One RS 1-Inch → 7680 × 3840
            pixels. High-end: Insta360 Pro 2 → 8000 × 4000 per lens, stitched to
            approximately 11520 × 5760 pixels. For most commercial virtual tour
            applications, a high-end smartphone running Travvir matches or
            exceeds entry-to-mid-range dedicated camera output.
          </p>
        </section>

        <section>
          <h2>Conclusion: Match Resolution to Platform and Purpose</h2>
          <p>
            The practical recommendation: shoot at maximum quality settings your
            phone supports, export at JPEG 90% quality, and check the file
            dimension before uploading to ensure it meets the platform minimum
            (minimum 6000 × 3000 for Google Street View quality threshold). For
            Travvir tours, the app handles optimisation automatically — just
            select the highest quality capture setting available in the app.
          </p>
          <p>
            Do not obsess over resolution beyond 8192 × 4096 for business
            virtual tour applications — beyond this point, the quality
            improvement is invisible to the majority of viewers on standard
            devices, and the file size increase creates unnecessary loading
            delays.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/fix-blurry-360-photos">Fix Blurry 360 Photos</a></li>
          <li><a href="/blog/en/what-is-a-360-photo">How 360 Photos Work</a></li>
          <li><a href="/blog/en/embed-360-virtual-tour-website">Embed Tours on Your Website</a></li>
          <li><a href="/blog/en/360-photography-mistakes-to-avoid">Avoid Common 360 Mistakes</a></li>
          <li><a href="/blog/en/best-smartphone-virtual-tour-app">Best Virtual Tour Apps</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-virtual-tour-image-size",
);

useBlogSeo({
  title: "360 Photo Size Guide: Best Resolution for Virtual Tours | Travvir",
  description:
    "Find the optimal 360 image resolution and file size for virtual tours. Covers pixel requirements, compression, and platform upload limits for 2026.",
  keywords:
    "360 image resolution, virtual tour image size, 360 photo resolution, equirectangular image size, 360 tour image specifications",
  slug: "360-virtual-tour-image-size",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
